import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Sprint 12+K-Hotfix7 (08.07.2026) — Pionier-Opt-In Landing für Klick-Tipp Smart-Link.
 *
 * URL-Pattern: /pionier-opt-in?email=…&s=soap_3&fn=Andreas
 * Klick-Tipp Smart-Link zeigt hierher. Frontend zeigt 1-Button-Card
 * ("Willkommen [Vorname]!" + "Ich bin dabei"). Auf Klick → POST an
 * /api/pionier/klick-tipp-opt-in → Success → Redirect nach /pionier-opt-in/danke.
 */
export default function PionierOptIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = (params.get("email") || "").trim().toLowerCase();
  const firstName = (params.get("fn") || "").trim();
  const soapStepRaw = (params.get("s") || "").replace(/^soap_?/, "");
  const soapStep = parseInt(soapStepRaw, 10);
  const source = params.get("s") ? "klick_tipp_soap" : "direct";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleOptIn = async () => {
    if (!email) {
      setError("Es fehlt eine E-Mail-Adresse in deinem Link. Bitte klicke den Link aus deiner E-Mail erneut.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/pionier/klick-tipp-opt-in", {
        email,
        first_name: firstName || undefined,
        source,
        soap_step: Number.isFinite(soapStep) && soapStep > 0 ? soapStep : undefined,
      });
      navigate("/pionier-opt-in/danke", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError("Bitte kurz warten und dann noch mal probieren (zu viele Anfragen).");
      } else if (status === 422) {
        setError("Die übergebene E-Mail-Adresse ist ungültig. Bitte klicke den Original-Link aus deiner E-Mail.");
      } else {
        setError(formatApiErrorDetail(err?.response?.data?.detail) || "Da ist gerade etwas schiefgelaufen. Bitte in ein paar Minuten erneut versuchen.");
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!email) {
      setError("Es fehlt eine E-Mail-Adresse in deinem Link. Bitte klicke den Link aus deiner E-Mail erneut.");
    }
  }, [email]);

  return (
    <div
      data-testid="pionier-opt-in-page"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #0b1220 0%, #111827 100%)",
        color: "#F1F5F9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: "100%",
          background: "rgba(15, 23, 42, 0.9)",
          borderRadius: 20,
          padding: "48px 40px",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
          border: "1px solid rgba(148, 163, 184, 0.15)",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            background: "#000",
            color: "#E11D48",
            fontWeight: 900,
            fontSize: 28,
            borderRadius: 12,
            marginBottom: 28,
            letterSpacing: -1,
          }}
        >
          4
        </div>

        <h1
          data-testid="pionier-opt-in-heading"
          style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15, margin: "0 0 16px 0" }}
        >
          {firstName ? `Willkommen, ${firstName}!` : "Willkommen!"}
        </h1>

        <p style={{ fontSize: 17, lineHeight: 1.5, color: "#CBD5E1", margin: "0 0 32px 0" }}>
          Ein Klick — und du bist offiziell Pionier von Flex4Success. Danach bekommst
          du deinen persönlichen Login-Link per E-Mail (7 Tage gültig).
        </p>

        {error && (
          <div
            data-testid="pionier-opt-in-error"
            style={{
              background: "rgba(239, 68, 68, 0.15)",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              padding: "12px 16px",
              borderRadius: 10,
              marginBottom: 20,
              fontSize: 14,
              color: "#FCA5A5",
            }}
          >
            {error}
          </div>
        )}

        <button
          data-testid="pionier-opt-in-cta"
          onClick={handleOptIn}
          disabled={loading || !email}
          style={{
            width: "100%",
            padding: "18px 24px",
            background: loading || !email ? "#475569" : "#E11D48",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            fontSize: 17,
            fontWeight: 700,
            cursor: loading || !email ? "not-allowed" : "pointer",
            transition: "background 200ms ease",
            letterSpacing: 0.2,
          }}
          onMouseEnter={(e) => {
            if (!loading && email) e.currentTarget.style.background = "#BE123C";
          }}
          onMouseLeave={(e) => {
            if (!loading && email) e.currentTarget.style.background = "#E11D48";
          }}
        >
          {loading ? "Einen Moment …" : "Ich bin dabei"}
        </button>

        <p
          data-testid="pionier-opt-in-email-hint"
          style={{ fontSize: 13, color: "#64748B", marginTop: 20, marginBottom: 0, textAlign: "center" }}
        >
          {email ? `Angemeldet als: ${email}` : "Keine E-Mail-Adresse übermittelt"}
        </p>
      </div>
    </div>
  );
}
